import React from 'react';
import i18n from '@/i18n/config';
import { Autocomplete, AutocompleteItem, Chip } from '@heroui/react';
import type { Key } from '@react-types/shared';

interface FilterEcommerceMultiSelectProps {
  label: string;
  value: boolean | null; // null = all, true = yes, false = no
  onChange: (value: boolean | null) => void;
  className?: string;
}

const ecommerceOptions = [
  { key: 'true', label: i18n.t('common.yes'), value: true },
  { key: 'false', label: i18n.t('common.no'), value: false }
];

export function FilterEcommerceMultiSelect({
  label,
  value,
  onChange,
  className = 'w-64'
}: FilterEcommerceMultiSelectProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedValue, setSelectedValue] = React.useState<boolean | null>(value);

  // Update selected value when value prop changes
  React.useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return ecommerceOptions;
    }
    const term = searchTerm.toLowerCase();
    return ecommerceOptions.filter(
      (opt) => opt.label.toLowerCase().includes(term) || opt.key.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  const handleValueAdd = React.useCallback(
    (key: Key | null) => {
      if (!key) return;

      const optionKey = String(key);
      const option = ecommerceOptions.find((opt) => opt.key === optionKey);

      if (!option) return;

      // If same value is selected, clear it (toggle off)
      if (selectedValue === option.value) {
        setSelectedValue(null);
        onChange(null);
      } else {
        setSelectedValue(option.value);
        onChange(option.value);
      }

      setSearchTerm('');
    },
    [selectedValue, onChange]
  );

  const handleValueRemove = React.useCallback(() => {
    setSelectedValue(null);
    onChange(null);
  }, [onChange]);

  const getSelectedLabel = () => {
    if (selectedValue === true) return 'Yes';
    if (selectedValue === false) return 'No';
    return null;
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label className="text-default-700 text-sm font-medium">{label}</label>
      <Autocomplete
        inputValue={searchTerm}
        onInputChange={setSearchTerm}
        selectedKey={selectedValue !== null ? (selectedValue ? 'true' : 'false') : null}
        onSelectionChange={handleValueAdd}
        isLoading={false}
        placeholder={i18n.t('copy.search')}
        size="sm"
        allowsCustomValue={false}
        onOpenChange={() => {}}
      >
        {filteredOptions.map((option) => (
          <AutocompleteItem key={option.key} textValue={option.label}>
            <div className="flex flex-col">
              <span className="font-medium">{option.label}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {selectedValue !== null && (
        <div className="mt-1 flex flex-wrap gap-1">
          <Chip key={selectedValue ? 'true' : 'false'} size="sm" variant="flat" onClose={handleValueRemove}>
            {getSelectedLabel()}
          </Chip>
        </div>
      )}
    </div>
  );
}

export default FilterEcommerceMultiSelect;
